<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  createStaffBriefing,
  listBriefingsForDiary,
  type SiteDiaryDetail,
} from "@/api/client";

const props = defineProps<{ diary: SiteDiaryDetail }>();

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const diaryId = computed(() => props.diary.id);

const { data: briefings, isPending } = useQuery({
  queryKey: ["diary-briefings", diaryId],
  queryFn: () => listBriefingsForDiary(diaryId.value),
});

const busy = ref(false);
const errorMsg = ref<string | null>(null);

function fmt(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function open(id: string): void {
  void router.push({
    name: "briefing-detail",
    params: { projectId: route.params.projectId, id },
  });
}

async function start(): Promise<void> {
  busy.value = true;
  errorMsg.value = null;
  try {
    const id = await createStaffBriefing({
      project_id: props.diary.project_id,
      project_name: props.diary.project_name,
      project_code: props.diary.project_code ?? undefined,
      briefing_date: props.diary.diary_date,
      rail_week: props.diary.rail_week ?? undefined,
      task_description: props.diary.site_description ?? undefined,
      briefer_name: "Site supervisor",
      diary_id: props.diary.id,
    });
    await queryClient.invalidateQueries({ queryKey: ["diary-briefings", diaryId] });
    open(id);
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <section class="rounded-xl bg-neutral-0 shadow p-6">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="font-semibold">
          Briefing &amp; sign-on
        </h2>
        <p class="text-sm text-neutral-500">
          Start the sign-on register for this shift — the gang signs on against it.
        </p>
      </div>
      <button
        type="button"
        class="shrink-0 rounded-md bg-brand-primary px-4 py-1.5 text-sm font-semibold text-brand-primary-on hover:opacity-90 disabled:opacity-50"
        :disabled="busy"
        @click="start"
      >
        {{ busy ? "Starting…" : "Start sign-on briefing" }}
      </button>
    </div>

    <p
      v-if="errorMsg"
      class="mt-2 text-sm text-semantic-danger"
    >
      {{ errorMsg }}
    </p>

    <div
      v-if="isPending"
      class="mt-3 text-sm text-neutral-400"
    >
      Loading…
    </div>
    <ul
      v-else-if="briefings?.length"
      class="mt-3 grid gap-2"
    >
      <li
        v-for="b in briefings"
        :key="b.id"
        class="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-neutral-200 px-4 py-2.5 text-sm hover:bg-neutral-50"
        @click="open(b.id)"
      >
        <span>
          <span class="font-medium text-neutral-900">Briefing — {{ fmt(b.briefing_date) }}</span>
          <span class="text-neutral-500"> · {{ b.signon_count }} signed on</span>
        </span>
        <span class="flex items-center gap-2">
          <span
            class="rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="b.delivered
              ? 'bg-semantic-success/10 text-semantic-success'
              : 'bg-semantic-warning/10 text-semantic-warning'"
          >{{ b.delivered ? "Delivered" : "Not delivered" }}</span>
          <span
            class="rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="b.status === 'closed'
              ? 'bg-neutral-100 text-neutral-600'
              : 'bg-brand-primary/10 text-brand-primary'"
          >{{ b.status === "closed" ? "Closed" : "Open" }}</span>
        </span>
      </li>
    </ul>
    <p
      v-else
      class="mt-3 text-sm text-neutral-400"
    >
      No briefing started for this shift yet.
    </p>
  </section>
</template>
